import React, { useEffect, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { actorCanAccessFeedbackInbox } from "../../feedback/feedbackInboxAccess";
import {
  countNewFeedback,
  feedbackReplyHref,
  feedbackReviewStatus,
  loadFeedbackInbox,
  setFeedbackReviewStatus,
  signFeedbackAttachments,
} from "../../feedback/feedbackInbox";
import { FeedbackImageViewer } from "./FeedbackImageViewer";
import "./feedback.css";

const TYPE_LABELS = {
  problem: "Problem",
  suggestion: "Suggestion",
  other: "Other",
};

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function FeedbackInboxScreen({ actor, onBack }) {
  const allowed = actorCanAccessFeedbackInbox(actor);
  const [filter, setFilter] = useState("new");
  const [rows, setRows] = useState(null);
  const [newCount, setNewCount] = useState(null);
  const [imageUrls, setImageUrls] = useState({});
  const [openPath, setOpenPath] = useState("");
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!allowed) {
      return undefined;
    }
    let cancelled = false;
    setRows(null);
    Promise.all([
      loadFeedbackInbox(undefined, filter),
      countNewFeedback().catch(() => null),
    ])
      .then(([data, count]) => {
        if (cancelled) return;
        setRows(data);
        setNewCount(count);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || "Feedback could not be loaded.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [allowed, filter]);

  useEffect(() => {
    if (!allowed || !rows?.length) {
      setImageUrls({});
      return undefined;
    }
    let cancelled = false;
    signFeedbackAttachments(rows.map((row) => row.attachment_path)).then((urls) => {
      if (!cancelled) setImageUrls(urls);
    });
    return () => {
      cancelled = true;
    };
  }, [allowed, rows]);

  async function changeStatus(row) {
    const next = feedbackReviewStatus(row.review_status) === "resolved" ? "new" : "resolved";
    setBusyId(row.feedback_id);
    setError("");
    try {
      await setFeedbackReviewStatus(row.feedback_id, next);
      setRows((current) => (current || []).filter((item) => item.feedback_id !== row.feedback_id));
      setNewCount((count) => {
        if (count == null) return count;
        return next === "resolved" ? Math.max(0, count - 1) : count + 1;
      });
    } catch (err) {
      setError(err?.message || "Feedback status could not be updated.");
    } finally {
      setBusyId("");
    }
  }

  return (
    <CrewPilotModuleShell
      compact
      title="Feedback Inbox"
      subtitle="Notes members send to Nexora."
      onBack={onBack}
      backLabel="Back"
      layout="form"
    >
      <div className="fb-screen">
        {allowed ? (
          <div className="fb-inbox-tabs" role="tablist" aria-label="Feedback to review">
            <button
              type="button"
              role="tab"
              aria-selected={filter === "new"}
              onClick={() => setFilter("new")}
            >
              New{newCount > 0 ? ` (${newCount})` : ""}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "resolved"}
              onClick={() => setFilter("resolved")}
            >
              Resolved
            </button>
          </div>
        ) : null}
        {!allowed ? (
          <p className="fb-inbox-note" role="status">
            Feedback Inbox opens when Nexora Systems is the active organization and you are an owner or admin.
          </p>
        ) : null}
        {allowed && error ? (
          <p className="fb-inbox-note" role="alert">{error}</p>
        ) : null}
        {allowed && !error && rows === null ? (
          <p className="fb-inbox-note">Loading feedback…</p>
        ) : null}
        {allowed && rows?.length === 0 ? (
          <p className="fb-inbox-note">
            {filter === "resolved" ? "No resolved feedback." : "No new feedback."}
          </p>
        ) : null}
        {allowed && rows?.length ? (
          <ul className="fb-inbox">
            {rows.map((row) => {
              const reply = feedbackReplyHref(row);
              const resolved = feedbackReviewStatus(row.review_status) === "resolved";
              const imageUrl = imageUrls[row.attachment_path];
              return (
                <li key={row.feedback_id} className="fb-inbox-card">
                  <div className="fb-inbox-meta">
                    <span>{TYPE_LABELS[row.feedback_type] || "Feedback"}</span>
                    <time dateTime={row.created_at}>{formatWhen(row.created_at)}</time>
                  </div>
                  <p>{row.message}</p>
                  {imageUrl ? (
                    <button
                      type="button"
                      className="fb-inbox-photo-btn"
                      aria-label="Open feedback screenshot"
                      onClick={() => setOpenPath(row.attachment_path)}
                    >
                      <img
                        className="fb-inbox-photo"
                        src={imageUrl}
                        alt="Feedback screenshot"
                      />
                    </button>
                  ) : row.attachment_path ? (
                    <p className="fb-inbox-context">Photo attached</p>
                  ) : null}
                  <p className="fb-inbox-context">
                    {row.source_screen || row.source_route || "CrewPilot"}
                    {resolved && row.resolved_at ? ` · Resolved ${formatWhen(row.resolved_at)}` : ""}
                  </p>
                  <div className="fb-inbox-actions">
                    {reply ? (
                      <a className="fb-inbox-reply" href={reply.href}>
                        Reply to {reply.email}
                      </a>
                    ) : null}
                    <button
                      type="button"
                      className="fb-inbox-status"
                      disabled={busyId === row.feedback_id}
                      onClick={() => changeStatus(row)}
                    >
                      {resolved ? "Reopen" : "Mark resolved"}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {openPath && imageUrls[openPath] ? (
          <FeedbackImageViewer
            src={imageUrls[openPath]}
            onClose={() => setOpenPath("")}
          />
        ) : null}
      </div>
    </CrewPilotModuleShell>
  );
}
